import { logger } from '../utils/logger';
import React, { useState } from 'react';
import { Recipe, Material, CraftedItem, MaterialSource, CharacterOption } from './Crafting';
import {
  getRarityIcon,
  getItemTypeFromResult, getItemTypeString, canCraftRecipe,
} from '../utils/craftingUtils';
import EquipmentIcon from './EquipmentIcon';

interface Props {
  recipes: Recipe[];
  inventory: Material[];
  craftedItems: CraftedItem[];
  materialSources: Record<number, MaterialSource[]>;
  characters: CharacterOption[];
  filterType: string;
  filterSlot: string;
  filterClass: string;
  filterMinLevel: string;
  filterMaxLevel: string;
  showOnlyCraftable: boolean;
  crafting: boolean;
  setFilterType: (v: string) => void;
  setFilterSlot: (v: string) => void;
  setFilterClass: (v: string) => void;
  setFilterMinLevel: (v: string) => void;
  setFilterMaxLevel: (v: string) => void;
  setShowOnlyCraftable: (v: boolean) => void;
  onCraftItem: (recipeId: number, characterId: number | null) => void;
}

const CraftingRecipesTab: React.FC<Props> = ({
  recipes, inventory, craftedItems, materialSources, characters,
  filterType, filterSlot, filterClass, filterMinLevel, filterMaxLevel, showOnlyCraftable, crafting,
  setFilterType, setFilterSlot, setFilterClass, setFilterMinLevel, setFilterMaxLevel, setShowOnlyCraftable, onCraftItem,
}) => {
  const [feeCharacterId, setFeeCharacterId] = useState<number | null>(characters[0]?.id ?? null);
  const characterId = feeCharacterId ?? characters[0]?.id ?? null;
  const selectedCharacterGold = characters.find(c => c.id === characterId)?.gold ?? 0;
  const getFilteredRecipes = (): Recipe[] => {
    return recipes.filter(recipe => {
      if (filterType !== 'all') {
        if (getItemTypeString(recipe.resultItemType).toLowerCase() !== filterType) return false;
      }
      if (filterSlot !== 'all') {
        const slotNumber = parseInt(filterSlot);
        logger.debug(`Filtering by slot ${slotNumber}, recipe ${recipe.name} has slot: ${recipe.resultEquipmentSlot}`);
        if (recipe.resultEquipmentSlot != null && recipe.resultEquipmentSlot !== slotNumber) {
          logger.debug(`  -> Filtered out: slot mismatch`);
          return false;
        }
        if (recipe.resultEquipmentSlot == null) {
          logger.debug(`  -> Filtered out: no slot defined`);
          return false;
        }
      }
      if (filterClass !== 'all' && recipe.requiredClass !== filterClass) return false;
      const level = recipe.requiredLevel || 1;
      if (filterMinLevel !== '' && level < parseInt(filterMinLevel)) return false;
      if (filterMaxLevel !== '' && level > parseInt(filterMaxLevel)) return false;
      if (showOnlyCraftable && !canCraftRecipe(recipe, inventory)) return false;
      return true;
    });
  };

  const filteredRecipes = getFilteredRecipes();

  return (
    <div className="crafting-tab-content">
      <div className="crafting-section-header crafting-section-header--gold">
        Known Recipes
      </div>

      <div className="crafting-filter-row">
        <select
          value={filterType}
          onChange={(e) => { setFilterType(e.target.value); setFilterSlot('all'); }}
          className="crafting-filter-select"
        >
          <option value="all">All Types</option>
          <option value="weapon">Weapons</option>
          <option value="armor">Armor</option>
          <option value="accessory">Accessories</option>
          <option value="consumable">Consumables</option>
        </select>

        <select value={filterSlot} onChange={(e) => setFilterSlot(e.target.value)} className="crafting-filter-select">
          <option value="all">All Slots</option>
          <optgroup label="Weapons">
            <option value="0">Main Hand</option>
            <option value="1">Off Hand</option>
          </optgroup>
          <optgroup label="Armor">
            <option value="2">Head</option>
            <option value="3">Chest</option>
            <option value="4">Legs</option>
            <option value="5">Feet</option>
            <option value="6">Hands</option>
          </optgroup>
          <optgroup label="Accessories">
            <option value="7">Necklace</option>
            <option value="8">Ring 1</option>
            <option value="9">Ring 2</option>
            <option value="10">Belt</option>
            <option value="11">Back</option>
          </optgroup>
        </select>

        <select value={filterClass} onChange={(e) => setFilterClass(e.target.value)} className="crafting-filter-select">
          <option value="all">All Classes</option>
          <option value="Warrior">Warrior</option>
          <option value="Mage">Mage</option>
          <option value="Ranger">Ranger</option>
          <option value="Cleric">Cleric</option>
          <option value="Rogue">Rogue</option>
        </select>

        <div className="crafting-filter-level">
          <input
            type="number"
            min={1}
            value={filterMinLevel}
            onChange={(e) => setFilterMinLevel(e.target.value)}
            placeholder="Min Lvl"
            className="crafting-filter-level-input"
          />
          <span className="crafting-filter-level-sep">–</span>
          <input
            type="number"
            min={1}
            value={filterMaxLevel}
            onChange={(e) => setFilterMaxLevel(e.target.value)}
            placeholder="Max Lvl"
            className="crafting-filter-level-input"
          />
        </div>

        <label className="crafting-filter-check">
          <input
            type="checkbox"
            checked={showOnlyCraftable}
            onChange={(e) => setShowOnlyCraftable(e.target.checked)}
          />
          Craftable only
        </label>
      </div>

      {characters.length > 0 && (
        <div className="cf-sell-character-select">
          <label htmlFor="cf-craft-character">Crafting as:</label>
          <select
            id="cf-craft-character"
            value={characterId ?? ''}
            onChange={(e) => setFeeCharacterId(Number(e.target.value))}
          >
            {characters.map(c => (
              <option key={c.id} value={c.id}>{c.name} ({c.gold ?? 0} gold)</option>
            ))}
          </select>
          <span className="crafting-fee-hint">Pays any gold smith's fee on high-tier recipes</span>
        </div>
      )}

      {filteredRecipes.length === 0 ? (
        <div className="crafting-empty">
          <p>No recipes found</p>
          <small>Discover recipes through exploration and experimentation</small>
        </div>
      ) : (
        <div className="crafting-recipes-list">
          {filteredRecipes.map(recipe => {
            const hasEnoughMaterials = canCraftRecipe(recipe, inventory);
            const goldFee = recipe.goldFee || 0;
            const canAffordFee = goldFee === 0 || (characterId !== null && selectedCharacterGold >= goldFee);
            const craftable = hasEnoughMaterials && canAffordFee;
            const craftedCount = craftedItems.filter(item => item.recipeName === recipe.name).length;
            const hasWeaponStats = recipe.minDamage || recipe.maxDamage;

            return (
              <div key={recipe.id} className={`crafting-recipe-card ${craftable ? 'craftable' : 'not-craftable'}`}>
                <div className="crafting-recipe-top">
                  <div className="crafting-recipe-icon">
                    <EquipmentIcon
                      imageUrl={recipe.imageUrl}
                      iconName={recipe.iconName}
                      itemName={recipe.name}
                      itemType={getItemTypeFromResult(recipe.resultItemType)}
                      size="large"
                      showTooltip={true}
                      stats={{
                        attackBonus: recipe.attackBonus,
                        defenseBonus: recipe.defenseBonus,
                        hpBonus: recipe.hpBonus,
                        speedBonus: recipe.speedBonus
                      }}
                    />
                  </div>

                  <div className="crafting-recipe-main">
                    <div className="crafting-recipe-name-row">
                      <span className="crafting-recipe-name">{recipe.name}</span>
                      <span className="cf-badge cf-badge--amber">{recipe.resultItemName}</span>
                      {craftedCount > 0 && (
                        <span className="cf-badge cf-badge--dim">×{craftedCount} owned</span>
                      )}
                    </div>

                    {recipe.requiredClass && (
                      <div className="crafting-recipe-class-label">
                        {recipe.requiredClass} · Lv.{recipe.requiredLevel}
                      </div>
                    )}

                    <p className="crafting-recipe-desc">{recipe.description}</p>

                    {(recipe.attackBonus > 0 || recipe.defenseBonus > 0 || recipe.hpBonus > 0 ||
                      recipe.speedBonus > 0) && (
                      <div className="crafting-recipe-stats-row">
                        {recipe.attackBonus > 0  && <span className="crafting-stat-chip">ATK +{recipe.attackBonus}</span>}
                        {recipe.defenseBonus > 0 && <span className="crafting-stat-chip">DEF +{recipe.defenseBonus}</span>}
                        {recipe.hpBonus > 0      && <span className="crafting-stat-chip">HP +{recipe.hpBonus}</span>}
                        {recipe.speedBonus > 0   && <span className="crafting-stat-chip">SPD +{recipe.speedBonus}</span>}
                      </div>
                    )}

                    {hasWeaponStats && (
                      <div className="crafting-recipe-weapon-block">
                        <div className="crafting-weapon-block-title">Weapon</div>
                        <div className="crafting-weapon-block-row">
                          {(recipe.minDamage || recipe.maxDamage) && (
                            <span>
                              {recipe.minDamage || 0}–{recipe.maxDamage || 0} dmg
                              {recipe.damageType ? ` (${recipe.damageType})` : ''}
                            </span>
                          )}
                          {(recipe.minRange || recipe.maxRange) && (
                            <span>Range {recipe.minRange || 1}–{recipe.maxRange || 1}</span>
                          )}
                          {recipe.weaponAPCost && recipe.weaponAPCost > 1 && (
                            <span>{recipe.weaponAPCost} AP</span>
                          )}
                          {recipe.aoeShape && recipe.aoeShape !== 'Single' && (
                            <span>AOE: {recipe.aoeShape}</span>
                          )}
                          {recipe.usesAmmo && (
                            <span>Ammo: {recipe.maxAmmo || 0}</span>
                          )}
                          {recipe.accuracyModifier !== undefined && recipe.accuracyModifier !== 0 && (
                            <span>Acc {recipe.accuracyModifier > 0 ? '+' : ''}{recipe.accuracyModifier}%</span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="crafting-ingredients-section">
                  <div className="crafting-ingredients-label">Required Materials</div>
                  {recipe.ingredients.map(ingredient => {
                    const available = inventory.find(i => i.materialId === ingredient.materialId)?.quantity || 0;
                    const hasEnough = available >= ingredient.quantity;
                    const sources = materialSources[ingredient.materialId] || [];
                    return (
                      <div key={ingredient.materialId} className="crafting-ingredient-row">
                        <span className="crafting-ingredient-name">
                          {getRarityIcon(ingredient.materialRarity)}
                          {ingredient.materialName}
                        </span>
                        <span className={`crafting-ingredient-qty ${hasEnough ? 'sufficient' : 'insufficient'}`}>
                          {available}/{ingredient.quantity}
                        </span>
                        <span className={`cf-badge ${hasEnough ? 'cf-badge--green' : 'cf-badge--red'}`}>
                          {hasEnough ? 'Ready' : 'Need more'}
                        </span>

                        <div className="cf-ingredient-tooltip">
                          {sources.length > 0 ? (
                            <>
                              <div className="cf-tooltip-header">Dropped by</div>
                              {sources.slice(0, 5).map((source, idx) => (
                                <div key={idx} className="cf-tooltip-source">
                                  <div className="cf-tooltip-monster">
                                    {source.monsterName} (Lv.{source.monsterLevel})
                                  </div>
                                  <div className="cf-tooltip-details">
                                    <span className={`cf-diff-badge ${source.monsterDifficulty.toLowerCase()}`}>
                                      {source.monsterDifficulty}
                                    </span>
                                    <span className="cf-tooltip-drop">{source.dropChance}% drop</span>
                                    <span className="cf-tooltip-qty">{source.minQuantity}–{source.maxQuantity}x</span>
                                  </div>
                                </div>
                              ))}
                              {sources.length > 5 && (
                                <div className="cf-tooltip-more">+{sources.length - 5} more sources</div>
                              )}
                            </>
                          ) : (
                            <div className="cf-tooltip-header">No known drop sources</div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="crafting-recipe-footer">
                  <div className="crafting-recipe-meta">
                    <span>{recipe.craftingTime}s craft time</span>
                    <span>{recipe.experienceReward} XP</span>
                    {goldFee > 0 && (
                      <span className={!canAffordFee ? 'crafting-fee-insufficient' : undefined}>
                        💰 {goldFee}g smith's fee
                      </span>
                    )}
                  </div>
                  <button
                    className="craft-btn"
                    onClick={() => onCraftItem(recipe.id, characterId)}
                    disabled={!craftable || crafting}
                    title={!hasEnoughMaterials ? 'Missing materials' : !canAffordFee ? `Need ${goldFee} gold for the smith's fee` : undefined}
                  >
                    {crafting ? 'Crafting…' : 'Craft Item'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CraftingRecipesTab;
